import React, { useEffect, useRef } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'

// Stripe promise instance - created once and reused
let stripePromise = null
let isStripeLoading = false

/**
 * Safely load Stripe with body element check
 * This prevents "Stripe.js requires that your page has a <body> element" error
 */
const loadStripeSafely = async () => {
    // Check if we're in browser environment
    if (typeof window === 'undefined') {
        throw new Error('Window is not available')
    }

    // Wait for body element to exist (handles SSR and fast navigation cases)
    const waitForBody = () => {
        return new Promise((resolve, reject) => {
            // If body already exists, resolve immediately
            if (document.body) {
                resolve()
                return
            }

            // Wait for body to appear (max 5 seconds)
            let attempts = 0
            const maxAttempts = 100 // 100 * 50ms = 5 seconds
            const checkInterval = setInterval(() => {
                attempts++
                if (document.body) {
                    clearInterval(checkInterval)
                    resolve()
                } else if (attempts >= maxAttempts) {
                    clearInterval(checkInterval)
                    reject(new Error('Body element not found after timeout'))
                }
            }, 50)
        })
    }

    await waitForBody()

    // Final check before loading Stripe
    if (!document.body) {
        throw new Error('Body element is not available')
    }

    return loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)
}

/**
 * Get or create Stripe promise instance
 * Uses singleton pattern to ensure only one Stripe instance exists
 */
const getStripePromise = () => {
    if (!stripePromise && !isStripeLoading) {
        isStripeLoading = true
        stripePromise = loadStripeSafely()
            .catch(error => {
                // Reset on error so we can retry later
                stripePromise = null
                isStripeLoading = false
                throw error
            })
            .finally(() => {
                isStripeLoading = false
            })
    }
    return stripePromise
}

/**
 * StripeProvider component wrapper
 * Handles Stripe initialization and cleanup safely
 */
const StripeProvider = ({ children, options }) => {
    const isMountedRef = useRef(true)
    const stripeInstanceRef = useRef(null)

    useEffect(() => {
        isMountedRef.current = true
        return () => {
            // Mark as unmounted to prevent operations after cleanup
            isMountedRef.current = false
            stripeInstanceRef.current = null
        }
    }, [])

    // Get Stripe promise with error handling
    const safeStripePromise = getStripePromise().catch(error => {
        // Check if it's a body-related error
        const isBodyError =
            error.message &&
            (error.message.includes('body') ||
                error.message.includes('IntegrationError') ||
                error.message.includes('requires that your page has a <body> element'))

        if (isBodyError && document.body) {
            // Reset promise to allow retry
            stripePromise = null
            return getStripePromise().catch(() => {
                // If retry fails, return null to prevent app crash
                // Elements component can handle null stripe gracefully
                return null
            })
        }

        // For other errors or if body doesn't exist, return null
        // This prevents app crash while allowing graceful degradation
        console.warn('Stripe loading failed, continuing without Stripe:', error.message)
        return Promise.resolve(null)
    })

    return (
        <Elements stripe={safeStripePromise} options={options}>
            {children}
        </Elements>
    )
}

export { getStripePromise }
export default StripeProvider
